import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchoolsOrder, sessionWhen, type PayBookingPage } from "./secure/payBooking";
import { RETURN_CANCELLED, RETURN_FAILED, recordReturnBody } from "./secure/stripeReturn";
import { CONFIRM_POLL_MS, pageUrl, useStripeReturn } from "./secure/useStripeReturn";
import { PaymentPendingNotice } from "./secure/SecurePaymentParts";
import { PartnerCheckout } from "./secure/PartnerCheckout";

// /secure/pay/booking/<payment-id>: a booking's payment link from the
// school's dashboard. A port of v1's page: the booking and its school, the
// already-paid and cancelled panels, and, while it's owed, paying it (v1
// hands over to its basket checkout; the element pays in place, through
// the same API calls).

export function SecurePayBookingView({ ctx, paymentId }: { ctx: ViewContext; paymentId: string }) {
  // A return from Stripe or a hosted Checkout, and the "paid, not showing
  // yet" mark that keeps Pay hidden for this order in this tab.
  const ret = useStripeReturn(`secure-pay-booking:${paymentId}`);
  const { returned, paid } = ret;

  const url = `${ctx.api}/app/pages/secure-payment/booking/${encodeURIComponent(paymentId)}`;
  const page = useQuery({
    queryKey: ["booking", "secure-pay-booking", ctx.api, paymentId],
    queryFn: () => bookingJson<PayBookingPage>(url),
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
    // After a payment, re-read until the order shows it, for a minute; then by hand.
    refetchInterval: (query) => (paid && ret.polling && query.state.data?.status === "PENDING" ? CONFIRM_POLL_MS : false),
  });

  // Back from Stripe (a card redirect or a hosted Checkout) with the money:
  // record it once, as the basket's return handler does (the webhook does
  // too, if this doesn't get through).
  const record = useMutation({
    mutationFn: (body: NonNullable<ReturnType<typeof recordReturnBody>>) =>
      sendJson(`${ctx.api}/widget/app/processBasketPayment`, "POST", body),
    onSettled: () => void page.refetch(),
  });
  const recorded = useRef(false);
  useEffect(() => {
    if (recorded.current || returned.outcome !== "paid") return;
    const body = recordReturnBody(paymentId, ret.returnSearch);
    if (!body) return;
    recorded.current = true;
    record.mutate(body);
  }, [returned, ret.returnSearch, paymentId, record]);

  const settled = !!paid && !!page.data && page.data.status !== "PENDING";
  useEffect(() => {
    if (settled) ret.settle();
  }, [settled, ret]);

  if (page.isLoading) return <BookingLoading rows={3} />;
  if (page.isError) {
    const status = page.error instanceof BookingApiError ? page.error.status : 0;
    return status === 404 ? (
      <RecordNotFound what="payment" />
    ) : (
      <BookingProblem
        title="We couldn’t load this payment"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }
  const data = page.data;
  if (!data || isOtherSchoolsOrder(ctx.businessId, ctx.businessSlug, data)) return <RecordNotFound what="payment" />;

  const school = data.school;
  const when = sessionWhen(data.session?.startDateTime ?? null, null, school?.timezone ?? null);
  // Paid, and the order doesn't show it yet: no Pay at all until it does.
  const confirming = !!paid && data.status === "PENDING";

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {school?.logoUrl && (
        <div className="flex justify-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={school.logoUrl} alt={school.name} className="max-h-20 max-w-full object-contain" />
        </div>
      )}
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{data.session?.title ?? "Your booking"}</h1>
        <p className="text-sm text-muted-foreground">with {school?.name}</p>
        {when && <p className="text-sm">{when}</p>}
      </header>

      {data.status === "SUCCEEDED" &&
        (paid ? (
          <Notice tone="success">
            <strong>Thank you, your payment has been received.</strong> Your booking is confirmed and we’ve sent a confirmation
            email.
          </Notice>
        ) : (
          <Notice tone="success">
            <strong>Already paid.</strong> Your booking is confirmed and we’ve sent a confirmation email. Nothing more to do —
            see you soon!
          </Notice>
        ))}

      {data.status === "CANCELLED" && (
        <Notice tone="info">
          <strong>This booking has been cancelled.</strong> Please contact {school?.name} if you’d like to rebook or if you have
          any questions.
        </Notice>
      )}

      {data.status === "PENDING" &&
        (confirming ? (
          <PaymentPendingNotice
            polling={ret.polling}
            schoolName={school?.name || "the school"}
            onCheck={() => void page.refetch()}
            checking={page.isFetching}
          />
        ) : (
          <>
            {returned.outcome === "failed" && <Notice tone="error">{RETURN_FAILED}</Notice>}
            {returned.outcome === "cancelled" && <Notice tone="info">{RETURN_CANCELLED}</Notice>}
            <PartnerCheckout
              ctx={ctx}
              page={data}
              returnUrl={pageUrl(ctx)}
              onPaid={() => {
                ret.markPaid(null);
                void page.refetch();
              }}
              onOrderMoved={() => void page.refetch()}
            />
          </>
        ))}
    </div>
  );
}
